import { Divider, Typography } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import React, { useCallback } from 'react';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import {
	Controls,
	ControlButton,
	ControlProps,
	useViewport,
	useReactFlow
} from 'reactflow';
import { ITheme } from 'types/theme';
import AutoLayoutControl from './AutoLayoutControl';

const ANIMATION_DURATION_MS = 100;

const CustomControls = (props: ControlProps) => {
	const { zoom } = useViewport();
	const { fitView, zoomIn, zoomOut } = useReactFlow();
	const theme = useTheme<ITheme>();

	const zoomOutCallback = useCallback(
		() => zoomOut({ duration: ANIMATION_DURATION_MS }),
		[zoomOut]
	);
	const zoomInCallback = useCallback(
		() => zoomIn({ duration: ANIMATION_DURATION_MS }),
		[zoomIn]
	);
	const fitCallback = useCallback(
		() => fitView({ duration: ANIMATION_DURATION_MS, maxZoom: 1 }),
		[fitView]
	);

	return (
		<Controls
			{...props}
			showInteractive={false}
			showFitView={false}
			showZoom={false}
			style={{ borderRadius: 5, left: 'unset', right: 15 }}
		>
			<Stack
				direction="row"
				sx={{ backgroundColor: theme.palette.background.paper }}
			>
				<ControlButton
					onClick={zoomOutCallback}
					data-testid="zoomout-button"
					aria-label={intl.tr('button.zoomOut')}
				>
					<Typography variant="inherit" component="span" color="text.secondary">
						<ZoomOutIcon width={16} />
					</Typography>
				</ControlButton>
				<Divider variant="middle" orientation="vertical" flexItem />
				<Typography
					pr={1}
					pl={1}
					sx={{ pointerEvents: 'none' }}
					alignSelf="center"
					textAlign="center"
					variant="body2"
					width={55}
					data-testid="zoom-info"
				>
					{Math.trunc(zoom * 100)}%
				</Typography>
				<Divider variant="middle" orientation="vertical" flexItem />
				<ControlButton
					onClick={zoomInCallback}
					data-testid="zoomin-button"
					aria-label={intl.tr('button.zoomIn')}
				>
					<Typography variant="inherit" component="span" color="text.secondary">
						<ZoomInIcon />
					</Typography>
				</ControlButton>
				<Divider variant="middle" orientation="vertical" flexItem />
				<ControlButton
					onClick={fitCallback}
					data-testid="fit-button"
					aria-label={intl.tr('button.pan')}
				>
					<Typography variant="inherit" component="span" color="text.secondary">
						<FitScreenIcon />
					</Typography>
				</ControlButton>
				<Divider variant="middle" orientation="vertical" flexItem />
				<AutoLayoutControl />
			</Stack>
		</Controls>
	);
};

export default CustomControls;
